import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

interface DisableDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sites: string[];
  /** `false` means "did not fully succeed" — the dialog stays open, reason kept. */
  onConfirm: (reason: string) => Promise<boolean>;
}

export function DisableDialog({ open, onOpenChange, sites, onConfirm }: DisableDialogProps) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const label = sites.length === 1 ? "Disable site" : `Disable ${sites.length} sites`;

  async function confirm() {
    setBusy(true);
    try {
      const ok = await onConfirm(reason.trim());
      if (ok) {
        setReason("");
        onOpenChange(false);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{label}</DialogTitle>
          <DialogDescription>
            The site stops running. The reason is saved on the site.
          </DialogDescription>
        </DialogHeader>
        <ul className="font-mono text-xs">
          {sites.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ul>
        <div className="grid gap-1.5">
          <Label htmlFor="disable-reason">Reason</Label>
          <Textarea
            id="disable-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="destructive"
            disabled={busy || !reason.trim()}
            onClick={() => void confirm()}
          >
            {label}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
